import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Copy, X } from 'lucide-react'
import type { CVRParticipant, GraphEdge, GraphNode, PersonDetails } from '../../../types'
import { graphNodeWatchlistEntity } from '../../../utils/watchlistEntity'
import { CountryFlagIcon } from '../../ui'
import WatchlistControl from '../../WatchlistControl'
import { buildGraphNodePanelData } from './graphNodePanelData'
import { GRAPH_PAN_THRESHOLD_PX } from './graphViewport'
import { ownershipChainSentence } from './chainSentence'
import { useToast } from '../../../contexts/ToastContext'

export interface GraphNodePanelProps {
  node: GraphNode
  rootId: string
  rootNavn: string
  /** All graph nodes — names for »Kæden i ord«. */
  nodes?: GraphNode[]
  edges: GraphEdge[]
  allParticipants: CVRParticipant[]
  beneficialOwners: CVRParticipant[]
  personDetails: PersonDetails | null
  personLoading: boolean
  companyStartdato?: string | null
  companySubsidiaryCount?: number | null
  companyBeneficialOwners?: CVRParticipant[]
  companyLoading?: boolean
  onClose: () => void
  viewportRef?: React.RefObject<HTMLDivElement | null>
}

function kindLabel(kind: GraphNode['kind']): string {
  switch (kind) {
    case 'person': return 'Person'
    case 'foreign-company': return 'Udenlandsk virksomhed'
    default: return 'Virksomhed'
  }
}

function profileHref(node: GraphNode): string | null {
  if (node.cvrNummer && !node.isRoot) return `/company/${node.cvrNummer}`
  if (node.enhedsNummer && node.kind === 'person') return `/person/${node.enhedsNummer}`
  return null
}

/** »Kæden i ord« — the chain as one copyable sentence. */
function ChainInWords({ sentence }: { sentence: string }) {
  const { showToast } = useToast()
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sentence)
      showToast('Kæden er kopieret.')
    } catch {
      showToast('Kunne ikke kopiere til udklipsholder.', { tone: 'signal' })
    }
  }
  return (
    <div className="rounded-lg bg-canvas px-3 py-2.5" data-testid="ownership-graph-node-chain">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-ink-500">Kæden i ord</span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-600 hover:bg-rule-soft hover:text-ink"
          aria-label="Kopiér kæden som tekst"
          title="Kopiér kæden som tekst"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <p className="mt-1 text-sm leading-[1.45] text-ink-700">{sentence}</p>
    </div>
  )
}

function PanelRow({
  label,
  children,
  testId,
}: {
  label: string
  children: React.ReactNode
  testId?: string
}) {
  return (
    <p className="text-[13px] leading-snug">
      <span className="text-ink-500">{label} </span>
      <span className="text-ink font-medium" data-testid={testId}>
        {children}
      </span>
    </p>
  )
}

function isDismissSuppressedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true
  if (target.closest('[data-graph-panel]')) return true
  if (target.closest('[data-graph-control]')) return true
  if (target.closest('[data-watchlist-menu]')) return true
  if (target.closest('svg [role="button"]')) return true
  return false
}

const GraphNodePanel: React.FC<GraphNodePanelProps> = ({
  node,
  rootId,
  rootNavn,
  nodes = [],
  edges,
  allParticipants,
  beneficialOwners,
  personDetails,
  personLoading,
  companyStartdato,
  companySubsidiaryCount,
  companyBeneficialOwners = [],
  companyLoading = false,
  onClose,
  viewportRef,
}) => {
  const panelRef = useRef<HTMLElement>(null)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const viewport = viewportRef?.current
    if (!viewport) return

    let downX = 0
    let downY = 0
    let pendingDismiss = false

    const onPointerDown = (e: PointerEvent) => {
      if (!viewport.contains(e.target as Node)) return
      if (isDismissSuppressedTarget(e.target)) {
        pendingDismiss = false
        return
      }
      pendingDismiss = true
      downX = e.clientX
      downY = e.clientY
    }

    const onPointerUp = (e: PointerEvent) => {
      if (!pendingDismiss) return
      pendingDismiss = false
      if (isDismissSuppressedTarget(e.target)) return
      const dx = e.clientX - downX
      const dy = e.clientY - downY
      if (Math.hypot(dx, dy) < GRAPH_PAN_THRESHOLD_PX) onClose()
    }

    viewport.addEventListener('pointerdown', onPointerDown)
    viewport.addEventListener('pointerup', onPointerUp)
    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown)
      viewport.removeEventListener('pointerup', onPointerUp)
    }
  }, [onClose, viewportRef])

  const data = buildGraphNodePanelData(
    node,
    rootId,
    edges,
    allParticipants,
    beneficialOwners,
    personDetails,
    companyStartdato,
    companySubsidiaryCount,
    companyBeneficialOwners,
  )
  const href = profileHref(node)
  const watchlistEntity = graphNodeWatchlistEntity(node)
  const showCountryFlag = !!data.countryCode
  const isCompany = node.kind === 'company' || node.kind === 'foreign-company'
  const chainSentence = node.isRoot
    ? null
    : ownershipChainSentence(node.id, rootId, nodes, edges)

  return (
    <div
      className={`pointer-events-auto transition-transform duration-200 ease-out ${
        entered ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <aside
        ref={panelRef}
        data-graph-panel
        data-testid="ownership-graph-node-panel"
        className="w-[min(360px,85vw)] max-h-[min(480px,60vh)] flex flex-col rounded-r-[10px] border border-rule bg-paper shadow-[0_8px_24px_rgba(18,21,26,0.12)]"
        aria-label={`Info om ${node.navn}`}
      >
        <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-rule shrink-0">
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold text-ink leading-snug truncate">
              {node.navn || '—'}
            </h3>
            <p className="flex items-center gap-1.5 text-[13px] text-ink-500 mt-0.5">
              {showCountryFlag && data.countryCode && (
                <CountryFlagIcon
                  code={data.countryCode}
                  testId="ownership-graph-node-country-flag"
                />
              )}
              <span>
                {kindLabel(node.kind)}
                {node.cvrNummer && ` · CVR ${node.cvrNummer}`}
                {node.enhedsNummer && node.kind === 'person' && ` · #${node.enhedsNummer}`}
              </span>
              {watchlistEntity && (
                <WatchlistControl
                  iconOnly
                  iconSize="sm"
                  menuPortal
                  entityType={watchlistEntity.entityType}
                  entityId={watchlistEntity.entityId}
                  displayName={node.navn || '—'}
                  countryCode={data.countryCode}
                />
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Luk"
            data-testid="ownership-graph-node-panel-close"
            className="shrink-0 p-1 text-ink-500 hover:text-ink hover:bg-rule-soft rounded-md"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-0">
          {node.isRoot && (
            <p className="text-[13px] text-ink-500">Dette er den viste virksomhed i grafen.</p>
          )}

          {chainSentence && <ChainInWords sentence={chainSentence} />}

          {!node.isRoot && data.beneficialStake && (
            <PanelRow label="Reelt ejerskab" testId="ownership-graph-node-beneficial">
              {data.beneficialStake} af {rootNavn}
            </PanelRow>
          )}

          {!node.isRoot && isCompany && data.rootOwnershipStakes.length > 0 && (
            <div className="text-[13px] leading-snug">
              <span className="text-ink-500">Ejerskabsandel af {rootNavn} </span>
              <span className="text-ink font-medium" data-testid="ownership-graph-node-root-stake">
                {data.rootOwnershipStakes.join(', ')}
              </span>
            </div>
          )}

          {!node.isRoot && isCompany && (
            <PanelRow label="Datterselskaber" testId="ownership-graph-node-subsidiaries">
              {companyLoading ? (
                <span className="text-ink-500 font-normal">Henter…</span>
              ) : data.subsidiaryCount != null ? (
                data.subsidiaryCount
              ) : (
                <span className="text-ink-500 font-normal">—</span>
              )}
            </PanelRow>
          )}

          {!node.isRoot && isCompany && node.cvrNummer && (
            <PanelRow label="Stiftet">
              {companyLoading ? (
                <span className="text-ink-500 font-normal">Henter…</span>
              ) : data.foundedDate ? (
                <span data-testid="ownership-graph-node-founded">{data.foundedDate}</span>
              ) : (
                <span className="text-ink-500 font-normal">—</span>
              )}
            </PanelRow>
          )}

          {!node.isRoot && isCompany && (
            <div className="text-[13px] leading-snug">
              <span className="text-ink-500">Reelle ejere </span>
              {companyLoading ? (
                <span className="text-ink-500 font-normal">Henter…</span>
              ) : data.companyBeneficialOwners.length > 0 ? (
                <span className="text-ink font-medium" data-testid="ownership-graph-node-company-bo">
                  {data.companyBeneficialOwners.join(', ')}
                </span>
              ) : (
                <span className="text-ink-500 font-normal" data-testid="ownership-graph-node-company-bo">
                  Ingen registrerede
                </span>
              )}
            </div>
          )}

          {!node.isRoot && data.roles.length > 0 && (
            <div className="text-[13px] leading-snug">
              <span className="text-ink-500">Rolle i selskabet </span>
              <span className="text-ink font-medium" data-testid="ownership-graph-node-roles">
                {data.roles.join(', ')}
              </span>
            </div>
          )}

          {!node.isRoot && data.involvedSince && (
            <PanelRow label="Involveret siden" testId="ownership-graph-node-involved-since">
              {data.involvedSince}
            </PanelRow>
          )}

          {!node.isRoot && node.kind === 'person' && (
            <PanelRow label="Aktiv i selskaber">
              {personLoading ? (
                <span className="text-ink-500 font-normal">Henter…</span>
              ) : data.activeCompanyCount != null ? (
                <span data-testid="ownership-graph-node-company-count">{data.activeCompanyCount}</span>
              ) : (
                <span className="text-ink-500 font-normal">—</span>
              )}
            </PanelRow>
          )}

          {node.truncated && (
            <p className="text-xs text-ink-500 leading-snug">
              Der kan være flere ejere eller datterselskaber end grafen viser.
            </p>
          )}
        </div>

        {href && (
          <div className="px-4 py-2.5 border-t border-rule shrink-0">
            <Link
              to={href}
              data-testid="ownership-graph-node-profile-link"
              className="text-[13px] font-semibold text-ink underline decoration-ink-300 underline-offset-[3px] hover:decoration-ink"
            >
              Åbn profil →
            </Link>
          </div>
        )}
      </aside>
    </div>
  )
}

export default GraphNodePanel
